/**
 * Makes Flickity claim a touch gesture only when it is predominantly horizontal.
 *
 * Flickity 3 starts a drag as soon as the pointer has travelled `dragThreshold`
 * pixels horizontally — `Math.abs(moveVector.x) > dragThreshold`, see
 * `flickity/js/drag.js` — and never compares that against vertical travel. A
 * mostly-vertical swipe that drifts 4px sideways therefore starts a carousel
 * drag, and Flickity's `handleDragMove` calls `event.preventDefault()`, so the
 * page stops scrolling while the thumb is over a carousel.
 *
 * Gating on `|x| > |y|` lets vertical swipes fall through to native page
 * scroll; once the browser commits to scrolling, Flickity's own scroll listener
 * ends the gesture. Genuine horizontal drags keep the original 3px
 * responsiveness, unlike the alternative of raising `dragThreshold` (which
 * delays every drag and still ignores vertical travel).
 *
 * Patched on the prototype so it covers every carousel in the theme, and called
 * from the only two modules that construct Flickity: `custom-elements/carousel.js`
 * and `hooks/useFlickity.ts`. An x-over-y gate is safe theme-wide because no
 * Flickity instance here scrolls vertically — `x-vertical-carousel` is
 * hand-rolled and does not use Flickity.
 */

import Flickity from 'flickity'

interface MoveVector {
  x: number
  y: number
}

// `hasDragStarted` is inherited from Unidragger and absent from @types/flickity.
interface DraggerProto {
  options: { dragThreshold: number }
  hasDragStarted: (this: DraggerProto, moveVector: MoveVector) => boolean
}

let isApplied = false

export function applyHorizontalDragAxis() {
  if (isApplied) return
  isApplied = true

  const proto = Flickity.prototype as unknown as DraggerProto

  proto.hasDragStarted = function (this: DraggerProto, moveVector: MoveVector) {
    return (
      Math.abs(moveVector.x) > this.options.dragThreshold &&
      Math.abs(moveVector.x) > Math.abs(moveVector.y)
    )
  }
}
